import { useIsMobile } from "@/hooks/use-mobile";
import {
  getRequiredFields,
  removeEmptyFields
} from "@/lib/utils";
import { routes } from "@/routes/utils";
import useAddInvoiceCodesMutation from "@/services/masterData/invoicecodes/useAddInvoiceCodeMutation";
import useGetInvoiceCodeDetailQuery from "@/services/masterData/invoicecodes/useInvoiceCodeDetailMutation";
import useUpdateInvoiceCodesMutation from "@/services/masterData/invoicecodes/useUpdateInvoiceCodeMutation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { DESCRIPTION, NAME } from "../consts";
import { formValues, schema } from "./validations";

const useAddEdit = () => {
  const { action,id } = useParams();
  const navigate = useNavigate();
  const isView = action === routes.view;
  const isEdit = action === routes.edit;
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
const [initialDetailPayload, setInitialDetailPayload] = useState<Record<
    string,
    any
  > | null>(null);

const { data: detailRes, isPending: isDetailLoading } =
    useGetInvoiceCodeDetailQuery(
      isEdit || isView ? String(id) : undefined
    );

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [NAME]: "",
      [DESCRIPTION]: "",
    },
  });

   useEffect(() => {
      if ((isEdit || isView) && detailRes?.data) {
        const d = detailRes.data;
        form.reset({
          [NAME]: String(d?.name ?? ""),
          [DESCRIPTION]: d?.description ? String(d?.description) : "",
        });
        // Store normalized initial payload for diffing on update
        setInitialDetailPayload({
          name: String(d?.name ?? ""),
          description: d?.description ? String(d?.description) : undefined,
        });
      }
    }, [detailRes, isEdit, isView, form]);

  const requiredFields = getRequiredFields(schema);

  const { mutateAsync: updateInvoiceCode, isPending: isUpdating } =
    useUpdateInvoiceCodesMutation({
      onSuccess: () => {
        toast.success("Invoice Code updated successfully");
        onBackClick();
      },
      onError: (error: any) => {
        toast.error(
          error.response.data?.issues?.message || error.response.data.message
            || "Failed to update Invoice Code"
        );
      },
    });

  const { mutate: addInvoiceCode, isPending, isError, error, isSuccess } =
    useAddInvoiceCodesMutation({
      onSuccess: () => {
        toast.success("Invoice Code added successfully");
        queryClient.invalidateQueries({ queryKey: ["invoiceCodes"] });
        navigate(`/${routes.admin}/${routes.masterData}`);
      },
      onError: (err) => {
        toast.error("Failed to add Invoice Code");
      },
    });

  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.masterData}`);
  };

  const onSubmit = async (data: formValues) => {
    let _data = removeEmptyFields(data);
    _data = removeEmptyFields(_data);
    if (isEdit && id) {
      await updateInvoiceCode({ id: String(id), data: _data });
    }else{
    await addInvoiceCode({ data: _data });
    }
    queryClient.invalidateQueries({ queryKey: ["invoiceCodes"] });
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isView,
    onCancel: onBackClick,
    isMobile,
    action,
    isEdit,
    isPending,
    isError,
    error,
    isSuccess,
  };
};

export default useAddEdit;